import { useState, useCallback, useMemo } from "react"
import { useMutation } from "@apollo/client"
import { useStaticQuery, graphql } from "gatsby"
import { useAppContext } from "@app/providers/app"
import { useShopify } from "@hooks/useShopify"
import { useAnalytics } from "@hooks/useAnalytics"
import { useCartContext } from "@providers/cart"
import { useCore } from "./useCore"

export const useCart = () => {
  const {
    helpers: { decodeShopifyId },
    graphql: {
      mutations: {
        CART_LINE_ADD, CART_LINE_UPDATE, CART_LINE_REMOVE, CART_LINE_REPLACE, CART_DISCOUNT_CODES_UPDATE, CART_ATTRIBUTES_UPDATE
      }
    }
  } = useCore()
  const { cart, id: cartId, saveCart } = useCartContext()
  const { cartNormaliser, formatMoney } = useShopify()
  const { trackCartUpdate } = useAnalytics()
  const { dispatch } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState([])

  const [cartLinesAdd] = useMutation(CART_LINE_ADD)
  const [cartLinesUpdate] = useMutation(CART_LINE_UPDATE)
  const [cartLinesRemove] = useMutation(CART_LINE_REMOVE)
  const [cartLinesReplace] = useMutation(CART_LINE_REPLACE)
  const [discountCodeUpdate] = useMutation(CART_DISCOUNT_CODES_UPDATE)
  const [cartAttributesUpdate] = useMutation(CART_ATTRIBUTES_UPDATE)

  const prepareCustomAttributes = useCallback(
    attributes =>
      attributes ? attributes?.map(({ key, value }) => ({
        key,
        value,
      })) : [],
    []
  )

  const addToCart = useCallback(
    async (product: any, variant: NormalisedVariant, quantity = 1, attributes: Attribute[] = []) => {
      setLoading(true)
      if (product?.variants?.length > 1 && attributes?.length === 0 && variant?.title) {
        attributes = [{ key: "_label", value: variant?.title }]
      }

      // Remove empty attributes if any, this causes an error in the cart
      attributes = attributes?.filter(({ value }) => !!value)

      const lines = {
        attributes:
          attributes?.map(({ key, value }) => ({
            key,
            value,
          })) || [],
        quantity: quantity,
        merchandiseId: variant.id,
      }

      const {
        data: { cartLinesAdd: data },
      } = await cartLinesAdd({
        variables: {
          cartId,
          lines,
        },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
        setLoading(false)
      }

      if (data?.cart) {
        // need to look at the normaliser
        const normalisedCart = cartNormaliser(data.cart)
        saveCart(data.cart)
        // trackPage(null, null, normalisedCart)
        dispatch({
          type: "cart",
          payload: true,
        })
        trackCartUpdate(
          "add",
          `gid://shopify/ProductVariant/${decodeShopifyId(variant.id, "ProductVariant")}`,
          quantity,
          normalisedCart?.lines
        )
      }

      setLoading(false)
    },
    [cartLinesAdd, cartId, cartNormaliser, trackCartUpdate, decodeShopifyId, saveCart, dispatch, setLoading]
  )

  const addToCartMultiple = useCallback(
    async (
      items: {
        variantId: string
        quantity: number
        attributes: Attribute[]
      }[] = []
    ) => {
      setLoading(true)
      const lines =
        items?.map(line => ({
          attributes: (line?.attributes as Attribute[])
            ?.filter(({ value }) => !!value)
            ?.map(({ key, value }) => ({
              key,
              value,
            })),
          quantity: line?.quantity,
          merchandiseId: line?.variantId,
        })) || []

      const {
        data: { cartLinesAdd: data },
      } = await cartLinesAdd({
        variables: {
          cartId,
          lines,
        },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
        setLoading(false)
      }

      if (data?.cart) {
        saveCart(data.cart)
        const normalisedCart = cartNormaliser(data.cart)
        // trackPage(null, null, normalisedCart)
        dispatch({
          type: "cart",
          payload: true,
        })
        for (const item of items) {
          trackCartUpdate("add", item.variantId, item.quantity, normalisedCart?.lines)
        }
      }
      setLoading(false)
    },
    [cartLinesAdd, cartId, cartNormaliser, saveCart, dispatch, trackCartUpdate, setLoading]
  )


  const removeFromCart = useCallback(
    async (id: string, variantId: string) => {
      setLoading(true)
      const quantity = cart?.lines.filter(line => line.id === id).map(({ quantity }) => quantity)[0] || 1

      const lineIds = cart?.lines.filter(line => line.id === id).map(line => line.id)

      const {
        data: { cartLinesRemove: data },
      } = await cartLinesRemove({
        variables: { cartId, lineIds },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
        setLoading(false)
      }

      if (data.cart) {
        const normalisedCart = cartNormaliser(data.cart)
        saveCart(data.cart)
        // trackPage(null, null, normalisedCart)
        trackCartUpdate("remove", variantId, quantity, cart?.lines || [])
      }
      setLoading(false)
    },
    [cart?.lines, trackCartUpdate, cartLinesRemove, cartId, cartNormaliser, saveCart, setLoading]
  )


  const updateQuantity = useCallback(
    async (id: string, variantId: string, quantity: number, action = "change") => {
      setLoading(true)

      const lines = cart?.lines
        .filter(line => line.id === id)
        .map(line => ({
          id: line.id,
          quantity: quantity,
        }))

      const {
        data: { cartLinesUpdate: data },
      } = await cartLinesUpdate({
        variables: { cartId, lines },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
        setLoading(false)
      }


      if (data?.cart) {
        const normalisedCart = cartNormaliser(data.cart)
        saveCart(data.cart)
        // trackPage(null, null, normalisedCart)
        trackCartUpdate(action, variantId, quantity, normalisedCart?.lines)
      }
      setLoading(false)
    },
    [saveCart, cart, cartLinesUpdate, cartId, cartNormaliser, trackCartUpdate, setLoading]
  )

  const updateLineAttributes = useCallback(
    async (id: string, attributes: Attribute[] = []) => {
      setLoading(true)

      const lines = cart?.lines
        .filter(line => line.id === id)
        .map(line => ({
          id: line.id,
          attributes: attributes ? prepareCustomAttributes(attributes?.customAttributes || attributes) : []
        }))

      const {
        data: { cartLinesUpdate: data },
      } = await cartLinesUpdate({
        variables: { cartId, lines },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
        setLoading(false)
      }

      if (data?.cart) {
        saveCart(data.cart)
      }
      setLoading(false)
    },
    [saveCart, cart, cartLinesUpdate, cartId, cartNormaliser, trackCartUpdate, setLoading]
  )

  const updateCartAttributes = useCallback(
    async (attributes: Attribute[] = []) => {
      setLoading(true)


      const {
        data: { cartAttributesUpdate: data },
      } = await cartAttributesUpdate({
        variables: { 
          cartId, 
          attributes: attributes ? prepareCustomAttributes(attributes?.customAttributes || attributes) : []
        },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
        setLoading(false)
      }

      if (data?.cart) {
        saveCart(data.cart)
      }
      setLoading(false)
    },
    [saveCart, cart, cartLinesUpdate, cartId, cartNormaliser, trackCartUpdate, setLoading]
  )

  const updateVariant = useCallback(
    async (id: string, variantId: string, quantity: number, action = "update") => {
      setLoading(true)

      const lines = cart?.lines
        .filter(line => line.id === id)
        .map(line => ({
          id: line.id,
          quantity: quantity,
          merchandiseId: variantId,
        }))

      const {
        data: { cartLinesUpdate: data },
      } = await cartLinesReplace({
        variables: { cartId, lines },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
        setLoading(false)
      }

      if (data?.cart) {
        const normalisedCart = cartNormaliser(data.cart)
        saveCart(data.cart)
        // trackPage(null, null, normalisedCart)
        trackCartUpdate(action, variantId, quantity, normalisedCart?.lines)
      }
      setLoading(false)
    },
    [saveCart, cart, cartLinesUpdate, cartId, cartNormaliser, trackCartUpdate, setLoading]
  )

  const applyDiscountCode = useCallback(
    async (discountCode: string) => {
      setLoading(true)

      // convert to array
      const discountCodes = [discountCode];
      // if adding a code, append all existing codes to array too
      // if removing code and passing empty string, don't ned to do this.
      if (discountCode) {
        cart?.discountCodes?.forEach((code) => {
          discountCodes.push(code?.code)
        })
      }

      const {
        data: { cartDiscountCodesUpdate: data },
      } = await discountCodeUpdate({
        variables: { cartId, discountCodes },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
        setLoading(false)
      }

      if (data?.cart) {
        saveCart(data.cart)
      }
      setLoading(false)
    },
    [saveCart, cart, cartLinesUpdate, cartId, cartNormaliser, trackCartUpdate, setLoading]
  )

  const { shipping } = useStaticQuery<GatsbyTypes.StaticShippingQuery>(graphql`
    query StaticShipping {
      shipping: sanitySettingShipping {
        threshold
        includeDiscounts
        messageBefore
        messageAfter
      }
    }
  `)

const freeShipping = useMemo(() => {
  const threshold = shipping?.threshold ?? 100
  const includeDiscounts = shipping?.includeDiscounts ?? true
  const total = includeDiscounts ? Number(cart?.cost?.subtotalAmount?.amount) : Number(cart?.cost?.totalAmount?.amount) || 0
  const percentage = total > threshold ? 100 : (total / threshold) * 100
  const remaining = total > threshold ? 0 : threshold - total
  const rawMessage = percentage === 100 ? shipping?.messageAfter : shipping?.messageBefore
  const message = rawMessage?.replace("{threshold}", formatMoney(threshold))?.replace("{remaining}", formatMoney(remaining))
  return { threshold, percentage, message }
}, [shipping, cart?.cost?.totalAmount?.amount, cart?.cost?.subtotalAmount?.amount, formatMoney])


  return {
    addToCart,
    addToCartMultiple,
    removeFromCart,
    updateQuantity,
    updateVariant,
    applyDiscountCode,
    updateLineAttributes,
    updateCartAttributes,
    freeShipping,
    loading,
    errors,
  }
}
